/**
 * The ONE place `@rive-app/react-canvas` and `react-native-web-lottie` are
 * imported (M2 spec §5.2).
 *
 * `cc-learning-app` will ship its own `AnimatedAsset.tsx` backed by
 * `rive-react-native` / `lottie-react-native`; every View above it only says
 * WHAT to animate (`kind`, `source`, `playing`, `loop`, `size`), never WHICH
 * runtime. That also keeps the classic lobby's promise from M2 §3.4: the Rive
 * instance is created ONCE and `playing` toggles `play()` / `pause()` on it, so
 * selecting a tile never remounts a canvas.
 *
 * `onRive` is the escape hatch the two progress cards need: the old
 * SearchProgress / ChallengeProgress components drive Rive TEXT RUNS
 * ("Opponent Name", "Title", "Game Details") and a state-machine input, which
 * cannot be expressed as props. The callback hands the raw instance out as
 * soon as it exists, and again whenever a NEW instance replaces it (FIX Y3 —
 * a `src`/artboard change re-creates `rive`, and the view driving the text
 * runs needs that one too); it is a no-op on a platform whose Rive has no
 * text runs.
 */
import React, {useEffect, useRef} from 'react';
import {StyleSheet, View} from 'react-native';
import {Alignment, Fit, Layout, useRive} from '@rive-app/react-canvas';
import LottieView from 'react-native-web-lottie';

export type AnimatedAssetKind = 'rive' | 'lottie';

export type AnimatedAssetProps = {
  kind: AnimatedAssetKind;
  /** whatever the bundler resolved `@/Assets/...` to (a url, a module, a json) */
  source: any;
  /** the square box the asset is drawn in */
  size: number;
  /** running (Rive: play/pause; Lottie: autoplay) */
  playing?: boolean;
  loop?: boolean;
  /** Rive only: the named animation or state machine to run */
  animation?: string;
  stateMachine?: string;
  artboard?: string;
  /** Rive only: fill the box (`Fit.Cover`) rather than fitting inside it */
  cover?: boolean;
  /** Rive only: the raw instance, for text runs and state-machine inputs */
  onRive?: (rive: any) => void;
};

function RiveAsset({
  source,
  size,
  playing,
  animation,
  stateMachine,
  artboard,
  cover,
  onRive,
}: AnimatedAssetProps) {
  const {RiveComponent, rive} = useRive({
    src: source,
    artboard,
    animations: animation,
    stateMachines: stateMachine,
    layout: new Layout({
      fit: cover ? Fit.Cover : Fit.Contain,
      alignment: Alignment.Center,
    }),
    // never autoplay: `playing` owns the transport, so the runtime chunk can
    // load after first paint without a frame of animation nobody asked for
    autoplay: false,
  });

  // FIX Y3: keyed on the INSTANCE, not a boolean — a re-created `rive` (a
  // `src`/artboard change) must reach `onRive` too, not just the first one.
  const handedRef = useRef<any>(null);
  useEffect(() => {
    if (rive && onRive && handedRef.current !== rive) {
      handedRef.current = rive;
      onRive(rive);
    }
  }, [rive, onRive]);

  useEffect(() => {
    if (!rive) {
      return;
    }
    try {
      if (playing) {
        rive.play();
      } else {
        rive.pause();
      }
    } catch (e) {
      // a runtime that has not finished loading its artboard yet; the next
      // `playing` change (or the load itself) settles it
    }
  }, [rive, playing]);

  return (
    <View style={[styles.box, {width: size, height: size}]}>
      <RiveComponent />
    </View>
  );
}

function LottieAsset({source, size, playing, loop}: AnimatedAssetProps) {
  return (
    <View style={[styles.box, {width: size, height: size}]}>
      <LottieView source={source} autoPlay={playing !== false} loop={!!loop} />
    </View>
  );
}

function AnimatedAsset(props: AnimatedAssetProps) {
  return props.kind === 'rive' ? (
    <RiveAsset {...props} />
  ) : (
    <LottieAsset {...props} />
  );
}

const styles = StyleSheet.create({
  box: {
    overflow: 'hidden',
  },
});

export default React.memo(AnimatedAsset);
